import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { Constants } from '../../core/constants';
import { Toro } from '../common/enumerations';
import { ActiveControllerInfo } from '../models/active-controller-info.model';
import { AuxiliaryInterpretedDataModel } from '../models/notifications/auxiliary-interpreted-data.model';
import { AuxiliaryManualStartNotificationModel } from '../models/notifications/auxiliary-manual-start-notification.model';
import { ControllerAllOffNotificationModel } from '../models/notifications/controller-all-off-notification.model';
import { ControllerRecordChangeNotificationModel } from '../models/notifications/controller-record-change-notification.model';
import { ControllerStatusChangedNotificationModel } from '../models/notifications/controller-status-changed-notification.model';
import { ProgramInterpretedDataModel } from '../models/notifications/program-interpreted-data.model';
import { ProgramManualStartNotificationModel } from '../models/notifications/program-manual-start-notification.model';
import { RunningModeChangeAcceptedNotificationModel } from '../models/notifications/runnning-mode-change-accepted-notification.model';
import { StationManualStartNotificationModel } from '../models/notifications/station-manual-start-notification.model';
import { StationInterpretedDataModel } from '../models/station-interpreted-data.model';
import { StationWithDurationModel } from '../models/station-with-duration.model';
import { ToroLocation } from '../models/toro-location.model';
import { ZoneProgressModel } from '../models/zone-progress.model';
import { ControllerManagerService } from './controller-manager.service';
import { LocationManagerService } from './location-manager.service';
import { NotificationManagerService } from './notification-manager.service';
import RunningStatus = Toro.RunningStatus;

@Injectable()
export class RuntimeStateManagerService implements OnDestroy
{
    public controllerOverStatusChange = new Subject<{ controllerId: number, programsSummary: string; stationsSummary: string; }>();
    public controllerAlertsChange = new Subject<{ controllerId: number, alerts: string[] }>();
    public controllerRunningModeChange = new Subject<{ controllerId: number, mode: Toro.RunningMode, daysOff: number }>();
    public stationsStatusChanged = new Subject<{ controllerId: number; stations: StationWithDurationModel[] }>();
    public controllerActiveStationChange = new Subject<ZoneProgressModel>();
    public controllerManualProgramStartStop = new Subject<ProgramManualStartNotificationModel>();
    public controllerManualAuxiliaryStart = new Subject<AuxiliaryManualStartNotificationModel>();
    public controllerManualStationStartStop = new Subject<StationManualStartNotificationModel>();
    public controllerProgramsInterpretedDataChanged = new Subject<{ controllerId: number, programsInterpretedData: ProgramInterpretedDataModel[] }>();
    public controllerAuxiliariesInterpretedDataChanged = new Subject<{ controllerId: number, auxiliariesInterpretedData: AuxiliaryInterpretedDataModel[] }>();

    // Subscriptions
    private controllerRecordChangeSub: Subscription;
    private controllerStatusChangeSub: Subscription;
    private controllerRunningModeChangeAcceptedSub: Subscription;
    private controllerManualProgramStartStopSub: Subscription;
    private controllerManualStationStartStopSub: Subscription;
    private currentLocationChangedSubscription: Subscription;
    private activeControllersForLocationSubscription: Subscription;

    // Dictionaries
    private controllerAlertsDict: { [controllerId: number]: string[]; } = {};
    private controllerRunningModeDict: { [controllerId: number]: { mode: Toro.RunningMode, daysOff: number } } = {};
    private stationsWithDurationDict: { [controllerId: number]: StationWithDurationModel[]; } = {};
    private controllerProgramsSummaryDict: { [controllerId: number]: string; } = {};
    private controllerStationsSummaryDict: { [controllerId: number]: string; } = {};
    private controllerManualProgramStartDict: { [controllerId: number]: ProgramManualStartNotificationModel; } = {};
    private controllerProgramsInterpretedData: { [controllerId: number]: ProgramInterpretedDataModel[]; } = {};
    private controllerAuxiliariesInterpretedData: { [controllerId: number]: AuxiliaryInterpretedDataModel[]; } = {};

    constructor(private notificationManager: NotificationManagerService,
                private locationManager: LocationManagerService,
                private controllerManager: ControllerManagerService)
    {
        this.setupSubscriptions();
    }

    ngOnDestroy()
    {
        if (this.controllerRecordChangeSub)
        {
            this.controllerRecordChangeSub.unsubscribe();
            this.controllerStatusChangeSub.unsubscribe();
            this.controllerRunningModeChangeAcceptedSub.unsubscribe();
            this.controllerManualProgramStartStopSub.unsubscribe();
            this.controllerManualStationStartStopSub.unsubscribe();
        }
        this.currentLocationChangedSubscription.unsubscribe();
        if(this.activeControllersForLocationSubscription){
            this.activeControllersForLocationSubscription.unsubscribe();
        }
    }

    private setupSubscriptions()
    {
        this.controllerRecordChangeSub = this.notificationManager.irrigationControllerRecordChange
            .subscribe((model: ControllerRecordChangeNotificationModel) =>
            {
                this.updateFromConfig(model.ControllerId, model.IncomingConfiguration);
            });

        this.controllerStatusChangeSub = this.notificationManager.irrigationControllerStatusChanged
            .subscribe((model: ControllerStatusChangedNotificationModel) =>
            {
                this.updateFromConfig(model.ControllerId, model.IncomingConfiguration);
            });

        this.controllerRunningModeChangeAcceptedSub = this.notificationManager.runningModeChangeAccepted
            .subscribe((model: RunningModeChangeAcceptedNotificationModel) =>
            {
                this.updateRunningMode(model.ControllerId, model.RunningMode, model.RunningModeDaysToRemainOff);
            });

        this.controllerManualProgramStartStopSub = this.notificationManager.irrigationControllerProgramStartStop
            .subscribe((model: ProgramManualStartNotificationModel) =>
            {
                this.controllerManualProgramStartDict[model.ControllerId] = (model.StartOrStop === 1) ? model : null;
                if (model.StartOrStop === 1)
                {
                    this.controllerManualProgramStartStop.next(model);
                }
            });

        this.controllerManualStationStartStopSub = this.notificationManager.irrigationControllerStationOnOff
            .subscribe((model: StationManualStartNotificationModel) =>
            {
                console.log('irrigationControllerStationOnOff ',model);
                if (model.OnOrOff === 1 && model.Duration.minutes() > 0)    //1 means started
                {
                    this.controllerManualStationStartStop.next(model);

                    // If we've already started the current station manually, ignore the irrigationControllerStationOnOff notification.
                    if(this.stationsWithDurationDict && this.stationsWithDurationDict[model.ControllerId]){
                        const currentSwd = this.stationsWithDurationDict[model.ControllerId][model.StationNumber - 1];
                        if (currentSwd && currentSwd.Station.Status === 'Running (Manual)')
                        {
                            return;
                        }
                    }
                        const station = { Status: 'Running (Manual)', Number: model.StationNumber, SecondsRemaining: model.Duration.asSeconds() };
                        this.stationsWithDurationDict[model.ControllerId] = [];
                        this.stationsWithDurationDict[model.ControllerId][model.StationNumber - 1] = new StationWithDurationModel(station);
                 } else {            //0 means stopped
                    this.controllerManualStationStartStop.next(model);
                }
            });

        this.currentLocationChangedSubscription = this.locationManager.currentLocationChange
            .subscribe(toroLocation => this.onCurrentLocationChanged(toroLocation));

    }

    private updateFromConfig(controllerId, config)
    {
        if (!config || !controllerId)
        {
            return;
        }

        // LoggingService.log(config, this);

        if (!config.RunningState)
        {
            return;
        }

        this.processInterpretedData(controllerId, config);
    }

    private processInterpretedData(controllerId: number, config: any)
    {
        const data = config.RunningState.InterpretedData;

        this.updateOverStatus(controllerId, data);

        this.updateControllerAlerts(controllerId, data.Alerts, data);

        // TODO: Update Auxiliary Status

        this.updateStationsStatus(controllerId, data.Stations);

        this.updateRunningMode(controllerId, config.RunningMode, config.RunningModeDaysToRemainOff);

        this.updateControllerProgramsInterpretedData(controllerId, data);

        this.updateControllerAuxiliariesInterpretedData(controllerId, data);
    }

    private updateOverStatus(controllerId: number, data: any)
    {
        // Program Summary
        this.controllerProgramsSummaryDict[controllerId] =
            (!data.ProgramSummary || data.ProgramSummary === 'Idle')
                ? Constants.DefaultIdleStateText
                : `${data.ProgramSummary} ${this.isManualProgram(data.Programs)}`;

        // Zone Summary
        if (!data.StationSummary || data.StationSummary === 'Idle')
        {
            // this.cleanupAfterActive();
            // this.controllerProgramsSummaryDict[controllerId] = Constants.DefaultIdleStateText;
            this.controllerStationsSummaryDict[controllerId] = Constants.DefaultIdleStateText;
        }
        else
        {
            this.controllerStationsSummaryDict[controllerId] = data.StationSummary;
        }

        this.controllerOverStatusChange.next({
            controllerId: controllerId,
            programsSummary: this.controllerProgramsSummaryDict[controllerId],
            stationsSummary: this.controllerStationsSummaryDict[controllerId]
        });
    }

    private updateControllerProgramsInterpretedData(controllerId: number, data: any)
    {
        if (!this.controllerProgramsInterpretedData[controllerId])
        {
            this.controllerProgramsInterpretedData[controllerId] = [];
        }

        for (let i = 0; i < data['Programs'].length; i++)
        {
            this.controllerProgramsInterpretedData[controllerId][i] = new ProgramInterpretedDataModel(data['Programs'][i]);
        }

        this.controllerProgramsInterpretedDataChanged.next({ controllerId, programsInterpretedData: this.controllerProgramsInterpretedData[controllerId] });
    }

    private updateControllerAuxiliariesInterpretedData(controllerId: number, data: any)
    {
        if (!this.controllerAuxiliariesInterpretedData[controllerId])
        {
            this.controllerAuxiliariesInterpretedData[controllerId] = [];
        }

        for (let i = 0; i < data['Auxiliaries'].length; i++)
        {
            this.controllerAuxiliariesInterpretedData[controllerId][i] = new AuxiliaryInterpretedDataModel(data['Auxiliaries'][i]);
        }

        this.controllerAuxiliariesInterpretedDataChanged.next({
            controllerId,
            auxiliariesInterpretedData: this.controllerAuxiliariesInterpretedData[controllerId]
        });
    }

    public checkForControllerOverStatus(controllerId: number): boolean
    {
        const programsSummary = this.controllerProgramsSummaryDict[controllerId];
        const stationsSummary = this.controllerStationsSummaryDict[controllerId];

        if (programsSummary && stationsSummary)
        {
            this.controllerOverStatusChange.next({
                controllerId: controllerId,
                programsSummary: programsSummary,
                stationsSummary: stationsSummary
            });

            return true;
        }

        return false;
    }

    public getControllerProgramsInterpretedData(controllerId: number): boolean
    {
        const controllerProgramsInterpretedData = this.controllerProgramsInterpretedData[controllerId];
        if (controllerProgramsInterpretedData)
        {
            this.controllerProgramsInterpretedDataChanged.next({ controllerId, programsInterpretedData: controllerProgramsInterpretedData });
            return true;
        }
        return false;
    }

    public getControllerAuxiliariesInterpretedData(controllerId: number): boolean
    {
        const controllerAuxiliariesInterpretedData = this.controllerAuxiliariesInterpretedData[controllerId];
        if (controllerAuxiliariesInterpretedData)
        {
            this.controllerAuxiliariesInterpretedDataChanged.next({ controllerId, auxiliariesInterpretedData: controllerAuxiliariesInterpretedData });
            return true;
        }
        return false;
    }

    private isManualProgram(programs: any)
    {
        for (let i = 0; i < programs.length; i++)
        {
            if (programs[i].IsManualStart)
            {
                return '(manual)';
            }
        }

        return '';
    }

    private updateControllerAlerts(controllerId: number, alerts: string[], data: any)
    {
        this.controllerAlertsDict[controllerId] = alerts;
        for (let i = 0; i < data.Stations.length; i++)
        {
            const station: StationInterpretedDataModel = data.Stations[i];
            if (station.Alerts && station.Alerts.length > 0)
            {
                station.Alerts.forEach((alert) =>
                {
                    this.controllerAlertsDict[controllerId].push(`${station.Description}: ${alert}`);
                });
            }
        }
        this.controllerAlertsChange.next({ controllerId, alerts });
    }

    public getControllerAlerts(controllerId: number)
    {
        return this.controllerAlertsDict[controllerId];
    }

    private updateRunningMode(controllerId: number, mode: Toro.RunningMode, daysOff: number)
    {
        if (mode === Toro.RunningMode.OffForDays)
        {
            mode = daysOff === 0 ? Toro.RunningMode.OffUntilNextSchedStart : Toro.RunningMode.OffForDays;
        }
        this.controllerRunningModeDict[controllerId] = { mode, daysOff };

        this.controllerRunningModeChange.next({ controllerId, mode, daysOff });
    }

    public getRunningMode(controllerId: number): { mode: Toro.RunningMode, daysOff: number }
    {
        return this.controllerRunningModeDict[controllerId];
    }

    private updateStationsStatus(controllerId: number, stations: any)
    {
        this.setStationsWithDurationDict(controllerId, stations);

        this.stationsStatusChanged.next({ controllerId, stations: this.stationsWithDurationDict[controllerId] });

        this.checkForActiveControllerStation(controllerId);
    }

    public setStationsWithDurationDict(controllerId: number, stations: any)
    {
        this.stationsWithDurationDict[controllerId] = this.stationsWithDurationDict[controllerId] || [];

        for (let i = 0; i < stations.length; i++)
        {
            if (this.stationsWithDurationDict[controllerId][i])
            {
                this.stationsWithDurationDict[controllerId][i].update(stations[i]);
            }
            else
            {
                this.stationsWithDurationDict[controllerId][i] = new StationWithDurationModel(stations[i]);
            }
        }
    }

    public checkForRunningStations(controllerId: number)
    {
        const stationsWithDurations = this.stationsWithDurationDict[controllerId];
        if (!stationsWithDurations || !stationsWithDurations.length)
        {
            return false;
        }

        this.stationsStatusChanged.next({ controllerId, stations: stationsWithDurations });
        return true;
    }

    public checkForActiveControllerStation(controllerId: number): boolean
    {
        const runningStations: ZoneProgressModel[] = [];
        const soakingStations: ZoneProgressModel[] = [];
        const stationsWithDurations = this.stationsWithDurationDict[controllerId];

        if (!stationsWithDurations || !stationsWithDurations.length)
        {
            return false;
        }

        stationsWithDurations.forEach(swd =>
        {
            if (swd.Station.Status.startsWith('Running') || swd.Station.Status === 'Soaking')
            {
                const model =
                    new ZoneProgressModel(
                        controllerId,
                        swd.Station.Number,
                        swd.TimeStamp,
                        swd.Duration,
                        swd.Station.SecondsRemaining,
                        null,
                        (swd.Station.Status.indexOf('(Manual)') > 0));

                if (swd.Station.Status.startsWith('Running'))
                {
                    model.zoneRunningState = Toro.ZoneRunningState.Watering;
                    runningStations.push(model);
                }
                else if (swd.Station.Status === 'Soaking')
                {
                    model.zoneRunningState = Toro.ZoneRunningState.Soaking;
                    soakingStations.push(model);
                }
            }
        });

        if (runningStations.length)
        {
            this.controllerActiveStationChange.next(runningStations[0]);
            return true;
        }
        else if (soakingStations.length)
        {
            this.controllerActiveStationChange.next(soakingStations[0]);
            return true;
        }
        else
        {
            this.controllerActiveStationChange.next(new ZoneProgressModel(controllerId, null));
        }

        return false;
    }

    public checkForActiveManualProgram(controllerId: number): boolean
    {
        const model = this.controllerManualProgramStartDict[controllerId];
        if (!model)
        {
            return false;
        }

        if (Date.now() - model.WhenForcedOn.getTime() <= (Constants.StaticManualProgramDurationInSec * 1000))
        {
            this.controllerManualProgramStartStop.next(model);
            return true;
        }

        this.controllerManualProgramStartDict[controllerId] = null;
        return false;
    }

    // =========================================================================================================================================================
    // Methods for triggering events to update UI in any interested components.
    // N.B., In some cases, Running State properties are also updated to reflect the proper UI Running State.
    //
    // @isTestingZones - Flag to determine if this method is being called as part of a Test All Zones action or a manual button press from user.
    // =========================================================================================================================================================
    public setManualStationStarted(controllerId: number, stationNumber: number, durationInSeconds: number, isTestingZones = false)
    {
        const station = { Status: 'Running (Manual)', Number: stationNumber, SecondsRemaining: durationInSeconds };

        this.stationsWithDurationDict[controllerId] = [];
        this.stationsWithDurationDict[controllerId][stationNumber - 1] = new StationWithDurationModel(station);

        // ================================================================================================
        // Emit Events
        // ================================================================================================

        // Stop all irrigation progress UI for current controller.
        if (!isTestingZones)
        {
            this.notificationManager.irrigationControllerAllOff.next(new ControllerAllOffNotificationModel({ ControllerId: controllerId }));
        }

        // Start the irrigation progress UI for the current controller/station combo.
        this.stationsStatusChanged.next({ controllerId, stations: this.stationsWithDurationDict[controllerId] });

        // Set the over status for the given controller/station combo.
        this.controllerOverStatusChange.next({
            controllerId: controllerId,
            programsSummary: Constants.DefaultIdleStateText,
            stationsSummary: `Active: ${stationNumber}`
        });
    }

    // =========================================================================================================================================================
    // Methods for triggering events to update UI in any interested components.
    // N.B., In some cases, Running State properties are also updated to reflect the proper UI Running State.
    // =========================================================================================================================================================
    public setManualProgramStarted(controllerId: number, programNumber: number)
    {
        // Populate program summary and manual program start dictionaries to facilitate proper update of UI when events are emitted below.
        // N.B., Typically server events will trigger the updating of these dictionaries, but in the case of a manual start from the UI,
        // we want to immediately update the UI w/o waiting for a server response (which can take a significant amount of time to arrive).
        const model = new ProgramManualStartNotificationModel({
            ControllerId: controllerId,
            WhenForcedOn: new Date(),
            StartOrStop: 1,
            ProgramNumber: programNumber
        });
        this.controllerManualProgramStartDict[controllerId] = model;
        this.controllerProgramsSummaryDict[controllerId] = `Active: ${programNumber}`;

        // ================================================================================================
        // Emit Events
        // ================================================================================================

        // Stop all irrigation progress UI for current controller.
        this.notificationManager.irrigationControllerAllOff.next(new ControllerAllOffNotificationModel({ ControllerId: controllerId }));

        // Start the irrigation progress UI for the current controller/program combo.
        this.controllerManualProgramStartStop.next(model);

        // Set the over status for the given controller/station combo.
        this.controllerOverStatusChange.next({
            controllerId: controllerId,
            programsSummary: `Active: ${programNumber} (manual)`,
            stationsSummary: Constants.DefaultIdleStateText
        });
    }

    public setControllerIrrigationStopped(controllerId: number)
    {
        // TODO: (?) May want to move into a CleanupAfterActive method (if reuired elsewhere in this class)
        this.stationsWithDurationDict[controllerId] = [];
        this.controllerProgramsSummaryDict[controllerId] = '';
        this.controllerStationsSummaryDict[controllerId] = '';
        this.controllerManualProgramStartDict[controllerId] = null;

        this.notificationManager.irrigationControllerAllOff.next(new ControllerAllOffNotificationModel({ ControllerId: controllerId }));
    }

    public setStationIrrigationStopped(controllerId: number, stationNumber:number)
    {
        // TODO: (?) May want to move into a CleanupAfterActive method (if reuired elsewhere in this class)
        this.stationsWithDurationDict[controllerId] = [];
        this.stationsWithDurationDict[controllerId][stationNumber - 1] = null;
        /* this.controllerProgramsSummaryDict[controllerId] = '';
        this.controllerStationsSummaryDict[controllerId] = '';
        this.controllerManualProgramStartDict[controllerId] = null; */

        this.notificationManager.irrigationControllerStationOnOff.next(
            new StationManualStartNotificationModel({ ControllerId: controllerId , StationNumber:stationNumber, OnOrOff: 0})
        );


        // Stop the irrigation progress UI for the current controller/station combo.
        //this.stationsStatusChanged.next({ controllerId, stations: this.stationsWithDurationDict[controllerId] });

        // Set the over status for the given controller/station combo.
        /* this.controllerOverStatusChange.next({
            controllerId: controllerId,
            programsSummary: Constants.DefaultIdleStateText,
            stationsSummary: `Active: ${stationNumber}`
        }); */

    }


    public setManualProgramStop(controllerId: number, programNumber: number)
    {
        // Populate program summary and manual program start dictionaries to facilitate proper update of UI when events are emitted below.
        // N.B., Typically server events will trigger the updating of these dictionaries, but in the case of a manual start from the UI,
        // we want to immediately update the UI w/o waiting for a server response (which can take a significant amount of time to arrive).
        const model = new ProgramManualStartNotificationModel({
            ControllerId: controllerId,
            WhenForcedOn: new Date(),
            StartOrStop: 0,
            ProgramNumber: programNumber
        });

        console.log('LN: this.controllerManualProgramStartDict[controllerId]  ', this.controllerManualProgramStartDict[controllerId]);
        console.log('LN: this.controllerProgramsSummaryDict[controllerId]  ', this.controllerProgramsSummaryDict[controllerId]);
        console.log('LN: this.controllerStationsSummaryDict[controllerId]  ', this.controllerProgramsSummaryDict[controllerId]);

        this.controllerManualProgramStartDict[controllerId] = null;
        this.controllerProgramsSummaryDict[controllerId] = '';
        //this.controllerStationsSummaryDict[controllerId] = '';

        // ================================================================================================
        // Emit Events
        // ================================================================================================

        // Stop program's irrigation progress UI for current controller.
        this.notificationManager.programStoppedNotification.next({ControllerId: controllerId, ProgramNumber: programNumber });

        // Stop the irrigation progress UI for the current controller/program combo.
        this.controllerManualProgramStartStop.next(model);

        // Set the over status for the given controller/station combo.
        this.controllerOverStatusChange.next({
            controllerId: controllerId,
            programsSummary: Constants.DefaultIdleStateText,
            stationsSummary: Constants.DefaultIdleStateText
        });



    }

    onCurrentLocationChanged(location: ToroLocation)
    {
        if (!location)
        {
            return;
        }

        const cachedActiveControllers = this.locationManager.activeControllers[location.Id]
            ? this.locationManager.activeControllers[location.Id].slice()
            : [];

        this.activeControllersForLocationSubscription = this.locationManager.getActiveControllersForLocation(location.Id, false).subscribe((activeControllers: ActiveControllerInfo[]) =>
        {
            this.updateDictionariesIfNeeded(cachedActiveControllers, activeControllers, location.Id);
            activeControllers.forEach((activeController: ActiveControllerInfo) =>
            {
                this.controllerManager.getControllerDetails(location.Id, activeController.ControllerId).subscribe((controller) =>
                {
                    if (!controller.Controller.IsControllerSupported)
                    {
                        this.updateStatusForNotSupportedControllers(activeController);
                    }
                });
            });
        });
    }

    public updateStatusForNotSupportedControllers(activeController: ActiveControllerInfo)
    {
        // Program Summary
        this.controllerProgramsSummaryDict[activeController.ControllerId] = activeController.getActiveProgramNumber() === -1
            ? Constants.DefaultIdleStateText
            : 'Active: ' + activeController.getActiveProgramNumber();

        // Station Summary
        this.controllerStationsSummaryDict[activeController.ControllerId] = activeController.getActiveStationNumber() === -1
            ? Constants.DefaultIdleStateText
            : 'Active: ' + activeController.getActiveStationNumber();

        this.controllerOverStatusChange.next({
            controllerId: activeController.ControllerId,
            programsSummary: this.controllerProgramsSummaryDict[activeController.ControllerId],
            stationsSummary: this.controllerStationsSummaryDict[activeController.ControllerId]
        });

        if (activeController.RunningPrograms.length > 0)
        {
            if (!this.controllerProgramsInterpretedData[activeController.ControllerId])
            {
                this.controllerProgramsInterpretedData[activeController.ControllerId] = [];
            }

            for (let i = 0; i < activeController.RunningPrograms.length; i++)
            {
                const activeProgram = activeController.getActiveProgram(activeController.RunningPrograms[i].RunningProgramNumber);
                this.controllerProgramsInterpretedData[activeController.ControllerId][i] = new ProgramInterpretedDataModel(
                    {
                        Number: activeProgram.RunningProgramNumber,
                        SecondsRemaining: activeProgram.RunningProgramRemainingSeconds,
                        IsRunning: activeProgram.RunningProgramRemainingSeconds > 0,
                        Status: RunningStatus[activeProgram.RunningProgramStatus]
                    });
            }

            this.controllerProgramsInterpretedDataChanged.next({
                controllerId: activeController.ControllerId,
                programsInterpretedData: this.controllerProgramsInterpretedData[activeController.ControllerId]
            });
        }

        if (activeController.RunningStations.length > 0)
        {
            if (!this.stationsWithDurationDict[activeController.ControllerId])
            {
                this.stationsWithDurationDict[activeController.ControllerId] = [];
            }
            for (let i = 0; i < activeController.RunningStations.length; i++)
            {
                const activeStation = activeController.getActiveStation(activeController.RunningStations[i].RunningStationNumber);
                if (this.stationsWithDurationDict[activeController.ControllerId][i])
                {
                    this.stationsWithDurationDict[activeController.ControllerId][i].update(
                        {
                            Number: activeStation.RunningStationNumber,
                            SecondsRemaining: activeStation.RunningStationRemainingSeconds,
                            IsRunning: activeStation.RunningStationRemainingSeconds > 0,
                            Status: RunningStatus[activeStation.RunningStationStatus]
                        });
                }
                else
                {
                    this.stationsWithDurationDict[activeController.ControllerId][i] = new StationWithDurationModel({
                        Number: activeStation.RunningStationNumber,
                        SecondsRemaining: activeStation.RunningStationRemainingSeconds,
                        IsRunning: activeStation.RunningStationRemainingSeconds > 0,
                        Status: RunningStatus[activeStation.RunningStationStatus]
                    });
                }
            }

            this.stationsStatusChanged.next({
                controllerId: activeController.ControllerId,
                stations: this.stationsWithDurationDict[activeController.ControllerId]
            });

            this.checkForActiveControllerStation(activeController.ControllerId);
        }
    }

    updateDictionariesIfNeeded(cacheControllers: ActiveControllerInfo[], activeControllers: ActiveControllerInfo[], locationId: number)
    {
        const notNeededControllers = cacheControllers.filter(function (item)
        {
            return activeControllers.findIndex(controller => controller.ControllerId === item.ControllerId) === -1;
        });
        notNeededControllers.forEach((controller) =>
        {
            this.controllerManager.getControllerDetails(locationId, controller.ControllerId).subscribe((toroController) =>
            {
                if (!toroController.Controller.IsRecognized)
                {
                    this.updateDictionaries(controller.ControllerId);
                }
            });
        });
    }

    updateDictionaries(controllerId)
    {
        // Overall status
        this.controllerProgramsSummaryDict[controllerId] = Constants.DefaultIdleStateText;
        this.controllerStationsSummaryDict[controllerId] = Constants.DefaultIdleStateText;
        this.controllerOverStatusChange.next({
            controllerId: controllerId,
            programsSummary: this.controllerProgramsSummaryDict[controllerId],
            stationsSummary: this.controllerStationsSummaryDict[controllerId]
        });

        // Program status
        this.controllerProgramsInterpretedData[controllerId] = [];
        this.controllerProgramsInterpretedDataChanged.next({
            controllerId: controllerId,
            programsInterpretedData: this.controllerProgramsInterpretedData[controllerId]
        });

        // Station status
        this.stationsWithDurationDict[controllerId] = [];
        this.stationsStatusChanged.next({
            controllerId: controllerId,
            stations: this.stationsWithDurationDict[controllerId]
        });
    }
}
